'use client'
import { PartialMessage } from '@bufbuild/protobuf'
import { disableQuery, useQuery } from '@connectrpc/connect-query'
import { GetPagePublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/page/v1/page_pb'
import { getSiteMembership } from '@fingertip/creator-proto/gen/fingertip/creator/site_membership/v1/site_membership-SiteMembershipService_connectquery'
import { getUserProfile } from '@fingertip/creator-proto/gen/fingertip/creator/user_profile/v1/user_profile-UserProfileService_connectquery'
import { BarsThreeIcon, CrossLargeIcon } from '@fingertip/icons'
import { type Session } from '@supabase/supabase-js'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RatingDialog } from '@/components/rating/rating-dialog'
import { UserButton } from '@/components/user/user-button'
import { useToken } from '@/lib/hooks/use-token'
import { getSupabaseClient } from '@/lib/supabase/supabase-client'

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { EditPageButton } from './edit-page-button'
import { PageNavbarLanguageSwitcher } from './page-navbar-language-switcher'
import { QrDialog } from './qr-dialog'
import { QrTrigger } from './qr-trigger'
import { SharePageButton } from './share-page-button'

type Props = {
  pageSlug: string
  siteSlug: string
  domain?: string
  data: PartialMessage<GetPagePublicResponse>
}

export const PageNavBar = ({ siteSlug, pageSlug, domain, data }: Props) => {
  const { t } = useTranslation()
  const searchParams = useSearchParams()
  const opengraph = searchParams?.get('opengraph')
  const utmSource = String(searchParams?.get('utm_source'))
  const isSource = [
    'apple-wallet',
    'google-wallet',
    'contact',
    'qr-block',
  ].includes(utmSource)
  const [session, setSession] = useState<Session | null>(null)
  const [open, setOpen] = useState(false)

  const { token, callOptions } = useToken()

  const { data: userData } = useQuery(
    getUserProfile,
    !token ? disableQuery : {},
    {
      callOptions,
    },
  )

  const { data: siteMembership } = useQuery(
    getSiteMembership,
    !token
      ? disableQuery
      : {
          siteSlug,
        },
    {
      callOptions,
    },
  )

  const currentUser = userData?.userProfile

  useEffect(() => {
    const getData = async () => {
      const supabase = getSupabaseClient()

      const {
        data: { session },
      } = await supabase.auth.getSession()

      setSession(session)
    }

    getData()
  }, [])

  if (!!opengraph || isSource) {
    return null
  }

  const pages = data?.pages || []

  return (
    <>
      <div className="fixed bottom-4 right-4 z-30">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger>
            <div className="flex rounded-full cursor-pointer size-12 items-center justify-center text-center transition-colors bg-background/30 hover:bg-background/40 active:bg-background/50 text-foreground shadow-md backdrop-blur-sm">
              {open ? (
                <CrossLargeIcon className="size-5 shrink-0" />
              ) : (
                <BarsThreeIcon className="size-5 shrink-0" />
              )}
            </div>
          </PopoverTrigger>

          <PopoverContent align="end" className="max-h-[350px] overflow-y-auto">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="h6">{t('actions')}</div>

                {!data?.site?.hideUserButton && (
                  <UserButton
                    currentUser={currentUser}
                    className="cursor-pointer"
                    avatarSize={32}
                    hideContactSupport
                    hideLanguageSwitcher
                  />
                )}
              </div>

              {!!siteMembership?.membership && (
                <EditPageButton siteSlug={siteSlug} pageSlug={pageSlug} />
              )}

              <SharePageButton
                siteSlug={siteSlug}
                isMember={!!siteMembership?.membership}
              />

              <QrTrigger />

              <RatingDialog
                siteSlug={siteSlug}
                pageSlug={pageSlug}
                session={session}
              />

              <PageNavbarLanguageSwitcher />

              {pages.length > 0 && <div className="my-1 h-px bg-input" />}

              {pages.length > 1 && <div className="h6">{t('pages')}</div>}

              {pages.map((page) => {
                let name = page?.name || `/${page?.slug}`
                let path = domain ? '/' : `/${siteSlug}/`

                if (page?.slug !== 'index') {
                  path += page?.slug
                } else {
                  name = 'Home'
                }

                return (
                  <Link
                    className="transition-colors line-clamp-1 truncate block hover:text-primary w-full"
                    href={path}
                    key={page?.id}
                  >
                    {name}
                  </Link>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <QrDialog
        siteSlug={siteSlug}
        domain={domain}
        pageSlug={pageSlug}
        utmSource={utmSource}
        hideFingertipBranding={data.site?.hideFingertipBranding}
      />
    </>
  )
}
